import React, { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useLang } from "../i18n/LanguageContext";
import { api } from "../lib/api";
import { ArrowRight, ArrowLeft, Check, Loader2, Star } from "lucide-react";
import { PROGRAMMES, PROGRAMMES_ORDER } from "../data/programmes";
import ApplicationModal from "../components/ApplicationModal";
import TestimonialsSection from "../components/TestimonialsSection";

const fmtChf = (n) => n ? `CHF ${n.toLocaleString("de-CH")}` : "";

// ─── Programme Index — list of 3 ───
export const ProgrammesIndex = () => {
  const { lang } = useLang();
  return (
    <div className="pt-28 min-h-screen" data-testid="programmes-index">
      <div className="container-sat pt-8 pb-12">
        <div className="overline mb-4">{lang === "en" ? "SAT · Premium Programmes" : "SAT · Premium-Programme"}</div>
        <h1 className="headline-xl text-5xl md:text-7xl">{lang === "en" ? "Three paths. One depth." : "Drei Wege. Eine Tiefe."}</h1>
        <p className="serif italic text-xl text-white/55 mt-6 max-w-2xl">{lang === "en" ? "For yourself, for your calling, for your company. Choose the path that matches the chapter you're in." : "Für dich. Für deine Berufung. Für dein Unternehmen. Wähle den Weg, der zu deinem Kapitel passt."}</p>
      </div>

      <section className="py-12 bg-[#050B1B]">
        <div className="container-sat grid lg:grid-cols-3 gap-6">
          {PROGRAMMES_ORDER.map((slug) => {
            const p = PROGRAMMES[slug];
            const t = p[lang] || p.de;
            return (
              <Link key={slug} to={`/programme/${slug}`} className="group relative overflow-hidden rounded-2xl border border-white/8 bg-[#0E1A30] hover:border-white/25 transition" data-testid={`programme-card-${slug}`}>
                <div className="aspect-[5/4] overflow-hidden relative">
                  <img src={p.bg} alt="" className="w-full h-full object-cover opacity-60 group-hover:opacity-80 group-hover:scale-105 transition duration-700" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0E1A30] via-[#0E1A30]/30 to-transparent"></div>
                </div>
                <div className="p-7">
                  <div className="overline mb-2" style={{ color: p.accent }}>{t.kicker}</div>
                  <h3 className="serif text-3xl font-light leading-tight">{t.title} <span className="italic">{t.title2}</span></h3>
                  <p className="text-white/55 text-sm mt-3 leading-relaxed">{t.headline}</p>
                  <div className="mt-5 flex items-center justify-between">
                    <div className="text-[#D4AF37]">{p.price_full_chf ? fmtChf(p.price_full_chf) : (lang === "en" ? "On request" : "Auf Anfrage")}</div>
                    <ArrowRight size={18} className="text-white/40 group-hover:text-[#D4AF37] group-hover:translate-x-1 transition" />
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </section>
    </div>
  );
};

// ─── Programme Detail / Sales Page ───
export const ProgrammeDetail = () => {
  const { slug } = useParams();
  const { lang } = useLang();
  const p = PROGRAMMES[slug];
  const [openApp, setOpenApp] = useState(false);
  const [openFaq, setOpenFaq] = useState(0);
  const [installments, setInstallments] = useState("full");
  const [busy, setBusy] = useState(false);

  if (!p) {
    return <div className="pt-32 text-center text-white/55">Not found.</div>;
  }
  const t = p[lang] || p.de;

  const priceFor = (mode) => {
    if (mode === "full") return p.price_full_chf;
    if (mode === "r3") return p.price_3rate_chf;
    return null;
  };
  const rateLabel = (mode) =>
    mode === "full" ? (lang === "en" ? "Single payment" : "Einmalzahlung") :
                      (lang === "en" ? "3 instalments" : "3 Raten");

  const startCheckout = async () => {
    if (!p.product_keys) return;
    const productKey = p.product_keys[installments] || p.product_keys.full;
    setBusy(true);
    try {
      const refCode = window.localStorage.getItem("sat_ref") || null;
      const r = await api.post("/payment/checkout", {
        product_key: productKey,
        origin_url: window.location.origin,
        referral_code: refCode,
      });
      window.location.href = r.data.url;
    } catch (e) {
      alert(e?.response?.data?.detail || "Fehler beim Starten der Bezahlung");
      setBusy(false);
    }
  };

  const programTitle = `${t.title} ${t.title2}`;
  const isInquiry = p.sales_mode === "inquiry_only";
  const isApp = p.sales_mode === "application_first";
  const wantsBoth = p.sales_mode === "checkout"; // master = direct + optional app

  return (
    <div className="pt-24 min-h-screen" data-testid={`programme-${slug}`}>
      {/* HERO */}
      <section className="relative min-h-[78vh] flex items-end overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img src={p.bg} alt="" className="w-full h-full object-cover object-center" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#081328] via-[#081328]/70 to-[#081328]/40"></div>
          <div className="absolute inset-0 bg-gradient-to-r from-[#081328] via-[#081328]/40 to-transparent"></div>
        </div>
        <div className="relative z-10 container-sat w-full pb-20">
          <Link to="/programme" className="btn-ghost text-sm inline-flex mb-8"><ArrowLeft size={14} /> {lang === "en" ? "All programmes" : "Alle Programme"}</Link>
          <div className="overline mb-4" style={{ color: p.accent }}>{t.kicker}</div>
          <h1 className="headline-caps text-[12vw] sm:text-[8vw] lg:text-[7rem] xl:text-[8rem]" style={{ lineHeight: 0.95 }}>
            <span className="block text-[#F7F6F2]">{t.title}</span>
            <span className="block headline-italic" style={{ textTransform: "none", fontStyle: "italic", fontWeight: 500, color: p.accent }}>{t.title2}</span>
          </h1>
          <p className="serif italic text-xl md:text-2xl text-white/75 mt-6 max-w-2xl">{t.lead}</p>
          <div className="mt-10 flex flex-col sm:flex-row gap-4">
            {(isApp || isInquiry) && (
              <button onClick={() => setOpenApp(true)} className="btn-primary pulse-gold" data-testid="programme-apply-cta">{t.cta_primary} <ArrowRight size={16} /></button>
            )}
            {wantsBoth && (
              <a href="#checkout" className="btn-primary pulse-gold" data-testid="programme-buy-cta">{t.cta_primary} <ArrowRight size={16} /></a>
            )}
            {wantsBoth && (
              <button onClick={() => setOpenApp(true)} className="btn-secondary" data-testid="programme-call-cta">{t.cta_secondary}</button>
            )}
            {!wantsBoth && !isInquiry && (
              <a href="#curriculum" className="btn-secondary">{t.cta_secondary}</a>
            )}
          </div>
        </div>
      </section>

      {/* PITCH */}
      <section className="py-24 bg-[#050B1B]">
        <div className="container-sat max-w-[1000px]">
          <p className="serif text-2xl md:text-3xl text-white/85 leading-relaxed dropcap">{t.pitch}</p>
        </div>
      </section>

      {/* WHO FOR */}
      <section className="py-24 bg-[#081328]">
        <div className="container-sat max-w-[1100px]">
          <div className="overline mb-4">{t.who_for_title}</div>
          <div className="grid md:grid-cols-2 gap-4 mt-8">
            {t.who_for.map((line, i) => (
              <div key={i} className="glass rounded-xl p-6 flex items-start gap-4" data-testid={`programme-whofor-${i}`}>
                <span className="num-mark" style={{ color: p.accent }}>0{i + 1}</span>
                <p className="text-white/80 leading-relaxed">{line}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PROMISES */}
      <section className="py-24 bg-[#050B1B]">
        <div className="container-sat">
          <div className="overline mb-3">{lang === "en" ? "What shifts" : "Was sich verändert"}</div>
          <h2 className="headline-xl text-4xl md:text-6xl max-w-3xl">{t.promise_title}</h2>
          <div className="grid md:grid-cols-2 gap-6 mt-12">
            {t.promises.map((pr, i) => (
              <div key={i} className="glass rounded-2xl p-7" data-testid={`programme-promise-${i}`}>
                <Star size={18} className="mb-3" style={{ color: p.accent }} />
                <div className="serif text-2xl mb-2">{pr.t}</div>
                <p className="text-white/70 leading-relaxed">{pr.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CURRICULUM */}
      <section id="curriculum" className="py-24 bg-[#081328]">
        <div className="container-sat">
          <div className="overline mb-3">{lang === "en" ? "Curriculum" : "Lehrplan"}</div>
          <h2 className="headline-xl text-4xl md:text-6xl">{t.curriculum_title}</h2>
          <div className="mt-12 divide-y divide-white/5 border-y border-white/5">
            {t.curriculum.map((m, i) => (
              <div key={i} className="flex flex-col md:flex-row md:items-center gap-2 md:gap-8 py-6" data-testid={`programme-mod-${i}`}>
                <div className="text-3xl serif text-white/30 md:w-16">{m.n}</div>
                <div className="md:w-1/3">
                  <div className="serif text-xl md:text-2xl font-light">{m.t}</div>
                </div>
                <div className="text-white/55 md:flex-1 text-sm leading-relaxed">{m.d}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* INCLUSIONS */}
      <section className="py-24 bg-[#050B1B]">
        <div className="container-sat max-w-[1100px]">
          <div className="overline mb-3">{lang === "en" ? "Included" : "Enthalten"}</div>
          <h2 className="headline-xl text-4xl md:text-5xl">{t.inclusions_title}</h2>
          <div className="grid md:grid-cols-2 gap-3 mt-10" data-testid="programme-inclusions">
            {t.inclusions.map((inc, i) => (
              <div key={i} className="flex items-start gap-3 p-4 rounded-xl bg-white/[0.02] border border-white/5">
                <Check size={18} className="shrink-0 mt-0.5" style={{ color: p.accent }} />
                <p className="text-white/80">{inc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING / CHECKOUT */}
      <section id="checkout" className="py-24 bg-[#081328]">
        <div className="container-sat max-w-[900px]">
          <div className="overline mb-3">{lang === "en" ? "Investment" : "Investition"}</div>
          {isInquiry ? (
            <div className="glass rounded-3xl p-10 text-center" data-testid="programme-inquiry-card">
              <div className="serif text-5xl text-[#D4AF37]">{lang === "en" ? "On request" : "Auf Anfrage"}</div>
              <p className="text-white/55 mt-4 max-w-md mx-auto">{lang === "en" ? "Every transformation is individual. We design your programme after a confidential first call." : "Jede Transformation ist individuell. Wir gestalten Ihr Programm nach einem vertraulichen Erstgespräch."}</p>
              <button onClick={() => setOpenApp(true)} className="btn-primary mt-8" data-testid="programme-inquiry-cta">{t.cta_primary} <ArrowRight size={16} /></button>
            </div>
          ) : (
            <>
              <h2 className="headline-xl text-4xl md:text-6xl">{lang === "en" ? "Choose your path." : "Wähle deinen Weg."}</h2>
              <div className="grid sm:grid-cols-3 gap-3 mt-10" data-testid="programme-price-options">
                {["full", "r3"].map((m) => (
                  <button
                    key={m}
                    onClick={() => setInstallments(m)}
                    className={`rounded-2xl p-6 text-left border transition ${installments === m ? "border-[#D4AF37] bg-[#D4AF37]/5" : "border-white/8 hover:border-white/20"}`}
                    data-testid={`programme-price-${m}`}
                  >
                    <div className="overline text-[#D4AF37]">{rateLabel(m)}</div>
                    <div className="serif text-3xl mt-2 font-light">{fmtChf(priceFor(m))}</div>
                    <div className="text-white/40 text-xs mt-1">{m === "full" ? (lang === "en" ? "Total" : "Gesamtpreis") : (lang === "en" ? `per month · ${m === "r3" ? "3 months" : "6 months"}` : `pro Monat · ${m === "r3" ? "3 Monate" : "6 Monate"}`)}</div>
                  </button>
                ))}
              </div>

              {p.bundle_with && p.bundle_price && (
                <div className="mt-6 rounded-2xl p-6 border border-[#4B2E83]/40 bg-[#4B2E83]/10" data-testid="programme-bundle">
                  <div className="overline text-[#D4AF37]">{lang === "en" ? "Bundle deal" : "Bundle-Angebot"}</div>
                  <p className="text-white/80 mt-2">{lang === "en" ? `Buy SAT Master + SAT Mentor together for ` : `SAT Master + SAT Mentor zusammen für `}
                    <b className="text-[#D4AF37]">CHF {p.bundle_price.toLocaleString("de-CH")}</b>{" "}
                    {lang === "en" ? `(save CHF ${(5990 + 7990 - p.bundle_price).toLocaleString("de-CH")}).` : `(spare CHF ${(5990 + 7990 - p.bundle_price).toLocaleString("de-CH")}).`}
                  </p>
                </div>
              )}

              <div className="flex flex-col sm:flex-row gap-3 mt-8">
                {wantsBoth ? (
                  <button onClick={startCheckout} disabled={busy} className="btn-primary pulse-gold flex-1 justify-center" data-testid="programme-checkout">
                    {busy ? <Loader2 className="animate-spin" size={16} /> : <>{lang === "en" ? "Continue to payment" : "Weiter zur Bezahlung"} <ArrowRight size={16} /></>}
                  </button>
                ) : (
                  <button onClick={() => setOpenApp(true)} className="btn-primary pulse-gold flex-1 justify-center" data-testid="programme-apply-pricing">
                    {t.cta_primary} <ArrowRight size={16} />
                  </button>
                )}
                <button onClick={() => setOpenApp(true)} className="btn-secondary" data-testid="programme-call-second">{lang === "en" ? "Talk to us first" : "Erst sprechen"}</button>
              </div>
            </>
          )}
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 bg-[#050B1B]">
        <div className="container-sat max-w-[900px]">
          <div className="overline mb-3">FAQ</div>
          <h2 className="headline-xl text-4xl md:text-5xl">{lang === "en" ? "The questions you probably have." : "Die Fragen, die du dir vermutlich stellst."}</h2>
          <div className="mt-10 space-y-2" data-testid="programme-faq">
            {t.faq.map((it, i) => (
              <div key={i} className="border-b border-white/8">
                <button onClick={() => setOpenFaq(openFaq === i ? -1 : i)} className="w-full py-5 text-left flex items-center justify-between gap-4">
                  <span className="serif text-lg md:text-xl">{it.q}</span>
                  <span className="text-[#D4AF37]">{openFaq === i ? "—" : "+"}</span>
                </button>
                {openFaq === i && <p className="pb-5 text-white/70 leading-relaxed">{it.a}</p>}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <TestimonialsSection programKey={p.key} accent={p.accent} language={lang} />

      {/* CLOSING */}
      <section className="py-24 bg-[#081328]">
        <div className="max-w-[900px] mx-auto px-6 text-center">
          <div className="serif italic text-3xl md:text-4xl text-[#D4AF37]">LOVE IS TRANSFORMATION</div>
          <p className="text-white/55 mt-6 max-w-xl mx-auto">{lang === "en" ? "Every great change begins with one single decision." : "Jede grosse Veränderung beginnt mit einer einzigen Entscheidung."}</p>
        </div>
      </section>

      <ApplicationModal
        open={openApp}
        onClose={() => setOpenApp(false)}
        programKey={p.key}
        programTitle={programTitle}
        variant={p.key === "sat_business" ? "business" : "personal"}
      />
    </div>
  );
};

export default ProgrammesIndex;
